import React from 'react';
import { FaCalendarAlt, FaMapMarkerAlt, FaImage } from 'react-icons/fa';
import { HiOutlineDocumentDuplicate } from 'react-icons/hi2';
import { useData } from '../../context/DataContext/DataProvider';
import { backendUrl } from '../../api/constants';
import './TemplateCard.css';

/**
 * TemplateCard - Matches the wizard review card design exactly
 * Used in both TemplatePicker and Templates page
 */
const TemplateCard = ({ 
  template, 
  onSelect, 
  onPost, 
  onEdit, 
  onDuplicate, 
  onDelete,
  variant = "default",
  compact = false 
}) => {
  const { globalConfig } = useData();
  const categories = globalConfig?.categories || [];
  
  const category = categories.find(c => c.id === template.category);
  const hasImages = template.images && template.images.length > 0;
  const coverImage = hasImages
    ? template.images[0]?.startsWith("http")
      ? template.images[0]
      : `${backendUrl}/assets/${template.images[0]}`
    : null;
  
  // Format date for display
  const formatDate = (dateObj) => {
    if (!dateObj?.start) return null;
    const d = new Date(dateObj.start);
    return d.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateObj) => {
    if (!dateObj?.start) return null;
    const d = new Date(dateObj.start);
    return d.toLocaleTimeString('en-US', { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).toLowerCase();
  };

  const handleCardClick = () => {
    if (onSelect) {
      onSelect(template);
    }
  };

  const showActions = onPost || onEdit || onDuplicate || onDelete;

  const normalizePrice = (price) => {
    if (!price) return "";
    const value = String(price).trim();
    if (!value || value === "$") return "";
    return value.includes("$") ? value : `$${value}`;
  };

  const formatTemplatePageDate = (dateObj) => {
    const date = formatDate(dateObj);
    if (!date) return null;
    return date.toLowerCase();
  };

  const getTemplatePageAddress = () => {
    if (!template.address) return null;
    return String(template.address)
      .replace(/,\s*undefined\s+undefined/gi, "")
      .replace(/\s+undefined\s+undefined/gi, "")
      .replace(/,\s*undefined/gi, "")
      .trim()
      .toLowerCase();
  };

  const getTicketLabel = () => {
    if (!template.ticketed) return "free event";

    const ticket = template.tickets?.find((item) => {
      return item?.priceType === "donation" || item?.priceType === "rsvp" || normalizePrice(item?.price) || normalizePrice(item?.priceMax);
    });

    if (!ticket) {
      const legacyPrice = normalizePrice(template.ticket_price);
      return legacyPrice ? `${legacyPrice} at the door` : "ticketed event";
    }

    const price = normalizePrice(ticket.price);
    const priceMax = normalizePrice(ticket.priceMax);

    if (ticket.priceType === "donation") return price || "donation";
    if (ticket.priceType === "rsvp") return price || "rsvp";

    if (ticket.priceType === "door") return price ? `${price} at the door` : "at the door";

    if (ticket.priceType === "range" && price && priceMax) {
      return `${price} - ${priceMax}`;
    }

    if (ticket.priceType === "starting_at" && price) {
      return `starting at ${price}`;
    }

    return price ? `${price} at the door` : "ticketed event";
  };

  if (variant === "templatesPage") {
    const displayDate = formatTemplatePageDate(template.date);
    const displayTime = formatTime(template.date);
    const displayAddress = getTemplatePageAddress();
    const ticketLabel = getTicketLabel();
    const templateTitle = template.templateName || "untitled template";

    return (
      <div
        className={`template-card-shell relative overflow-hidden rounded-kit-lg border border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] ${onSelect ? 'cursor-pointer transition-colors hover:border-[var(--inyc-secondary)]' : ''}`}
        onClick={onSelect ? handleCardClick : undefined}
      >
        <div className="border-b border-[var(--inyc-input-border-color)] px-kit-16 py-kit-12">
          <div className="font-['Primary-Bold'] text-kit-sm leading-[1.15] text-[var(--inyc-primary-text-color)]">
            {templateTitle}
          </div>
        </div>

        {hasImages ? (
          <div className="h-[132px] w-full overflow-hidden bg-[var(--inyc-primary-light)]">
            <img
              src={coverImage}
              alt={template.name || "template"}
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <div className="flex h-[132px] w-full items-center justify-center bg-[var(--inyc-primary-light)]">
            <FaImage size={30} className="opacity-20" />
          </div>
        )}

        <div className="p-kit-16 pb-kit-12">
          <div className="flex items-start justify-between gap-kit-12">
            <h2 className="min-w-0 font-['Primary-Bold'] text-kit-base leading-[1.2] text-[var(--inyc-primary-text-color)]">
              {template.name || <span className="opacity-40 italic">untitled event</span>}
            </h2>
            {category && (
              <div
                className="inline-flex shrink-0 items-center gap-kit-6 rounded-kit-full px-kit-10 py-kit-4 text-kit-2xs font-['Primary-Bold'] leading-none"
                style={{
                  backgroundColor: `${category.activeColor}20`,
                  color: category.activeColor
                }}
              >
                <span
                  className="h-[6px] w-[6px] rounded-kit-full"
                  style={{ backgroundColor: category.activeColor }}
                />
                {category.id}
              </div>
            )}
          </div>

          <div className="mt-kit-8 flex items-center gap-kit-8 text-kit-xs leading-[1.25] text-[var(--inyc-primary-text-color-light)]">
            <FaCalendarAlt size={12} className="shrink-0 opacity-70" />
            <span>
              {displayDate || <span className="opacity-40 italic">no date</span>}
              {template.date?.start && (
                <span className="ml-kit-6 opacity-80">
                  {displayTime}
                </span>
              )}
            </span>
          </div>

          <div className="mt-kit-4 flex items-start gap-kit-8 text-kit-xs leading-[1.25] text-[var(--inyc-primary-text-color-light)]">
            <FaMapMarkerAlt size={12} className="mt-kit-2 shrink-0 opacity-70" />
            <span>{displayAddress || <span className="opacity-40 italic">no location</span>}</span>
          </div>

          <div className="mt-kit-6 text-kit-xs leading-none text-[var(--inyc-primary-text-color-light)]">
            {ticketLabel}
          </div>
        </div>

        {showActions && !compact && (
          <div className="flex items-center justify-between px-kit-16 pb-kit-16">
            {onPost && (
              <button
                className="flex h-[32px] w-[5.5rem] items-center justify-center rounded-kit-full bg-[var(--inyc-secondary)] px-kit-8 text-kit-xs font-['Primary-Bold'] text-[var(--inyc-secondary-text-color)] transition-opacity hover:opacity-90"
                onClick={(e) => { e.stopPropagation(); onPost(template); }}
              >
                post
              </button>
            )}
            {onEdit && (
              <button
                className="flex h-[32px] w-[5.5rem] items-center justify-center rounded-kit-full border border-black bg-white px-kit-8 text-kit-xs font-['Primary-Bold'] text-black transition-colors hover:bg-white"
                onClick={(e) => { e.stopPropagation(); onEdit(template); }}
              >
                edit
              </button>
            )}
            {onDuplicate && (
              <button
                className="flex h-[32px] w-[5.5rem] items-center justify-center rounded-kit-full border border-[var(--inyc-outline-color)] bg-[color-mix(in_srgb,var(--inyc-input-background-color),black_12%)] px-kit-8 text-kit-xs font-['Primary-Bold'] text-[var(--inyc-primary-text-color)] transition-colors hover:bg-[color-mix(in_srgb,var(--inyc-input-background-color),black_18%)] [:is(body.dark)_&]:border-[var(--inyc-outline-color)]"
                onClick={(e) => { e.stopPropagation(); onDuplicate(template); }}
              >
                duplicate
              </button>
            )}
            {onDelete && (
              <button
                className="flex h-[32px] w-[5.5rem] items-center justify-center rounded-kit-full border border-transparent bg-danger-strong px-kit-8 text-kit-xs font-['Primary-Bold'] text-on-fill transition-opacity hover:opacity-90"
                onClick={(e) => { e.stopPropagation(); onDelete(template); }}
              >
                delete
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div 
      className={`relative rounded-kit-lg border border-[var(--inyc-input-border-color)] overflow-hidden bg-[var(--inyc-primary)] ${onSelect ? 'cursor-pointer hover:border-[var(--inyc-secondary)] transition-all' : ''}`}
      onClick={onSelect ? handleCardClick : undefined}
    >
      {/* Template badge */}
      <div className="px-kit-10 py-kit-4 bg-[rgba(0,0,0,0.85)] text-white inline-flex items-center gap-kit-6 text-kit-2xs font-['Primary-Medium'] m-kit-8 rounded-kit-lg absolute z-10 backdrop-blur-[4px]">
        <HiOutlineDocumentDuplicate size={12} className="text-on-fill" />
        <span className="text-on-fill">{template.templateName}</span>
      </div>
      
      {/* Image Section - matches wizard review */}
      {hasImages ? (
        <div className="w-full h-[180px] bg-[var(--inyc-primary-light)] overflow-hidden relative">
          <img 
            src={coverImage}
            alt="Event"
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="w-full h-[100px] bg-[var(--inyc-primary-light)] flex items-center justify-center relative">
          <FaImage size={32} className="opacity-20" />
        </div>
      )}
      
      {/* Content Section - matches wizard review exactly */}
      <div className="p-kit-16">
        {/* Category pill */}
        {category && (
          <div 
            className="inline-flex items-center gap-kit-6 px-kit-10 py-kit-4 rounded-kit-lg text-kit-2xs font-['Primary-Medium'] mb-kit-8"
            style={{ 
              backgroundColor: `${category.activeColor}20`,
              color: category.activeColor
            }}
          >
            <span 
              className="w-[6px] h-[6px] rounded-kit-full"
              style={{ backgroundColor: category.activeColor }}
            />
            {category.id}
          </div>
        )}
        
        {/* Title */}
        <h2 className="text-kit-lg font-['Primary-Bold'] text-[var(--inyc-primary-text-color)] mb-kit-8">
          {template.name || <span className="opacity-40 italic">untitled event</span>}
        </h2>
        
        {/* Date & Time */}
        <div className="flex items-center gap-kit-8 text-[var(--inyc-primary-text-color)] mb-kit-4">
          <FaCalendarAlt size={14} className="opacity-60" />
          <span className="text-kit-sm">
            {formatDate(template.date) || <span className="opacity-40 italic">no date</span>}
            {template.date?.start && (
              <span className="opacity-70 ml-kit-8">
                {formatTime(template.date)}
              </span>
            )}
          </span>
        </div>
        
        {/* Location */}
        <div className="flex items-center gap-kit-8 text-[var(--inyc-primary-text-color)] mb-kit-8">
          <FaMapMarkerAlt size={14} className="opacity-60" />
          <span className="text-kit-sm">
            {template.address || <span className="opacity-40 italic">no location</span>}
          </span>
        </div>
        
        {/* Ticket info */}
        {template.ticketed && template.tickets && template.tickets.length > 0 ? (
          <div className="text-kit-xs text-[var(--inyc-primary-text-color)] opacity-70">
            {template.tickets.length} ticket type{template.tickets.length > 1 ? 's' : ''}
          </div>
        ) : (
          <div className="text-kit-xs text-[var(--inyc-primary-text-color)] opacity-70">
            free event
          </div>
        )}
      </div>
      
      {/* Actions Section */}
      {showActions && !compact && (
        <div className="flex items-center justify-between px-kit-16 py-kit-12 border-t border-[var(--inyc-input-border-color)]">
          {onPost && (
            <button 
              className="flex h-[32px] w-[5.5rem] items-center justify-center rounded-kit-full bg-[var(--inyc-secondary)] px-kit-8 text-kit-xs font-['Primary-Bold'] text-[var(--inyc-secondary-text-color)] transition-opacity hover:opacity-90"
              onClick={(e) => { e.stopPropagation(); onPost(template); }}
            >
              post
            </button>
          )}
          {onEdit && (
            <button 
              className="flex h-[32px] w-[5.5rem] items-center justify-center rounded-kit-full border border-black bg-white px-kit-8 text-kit-xs font-['Primary-Bold'] text-black transition-colors hover:bg-white"
              onClick={(e) => { e.stopPropagation(); onEdit(template); }}
            >
              edit
            </button>
          )}
          {onDuplicate && (
            <button 
              className="flex h-[32px] w-[5.5rem] items-center justify-center rounded-kit-full border border-[var(--inyc-outline-color)] bg-[color-mix(in_srgb,var(--inyc-input-background-color),black_12%)] px-kit-8 text-kit-xs font-['Primary-Bold'] text-[var(--inyc-primary-text-color)] transition-colors hover:bg-[color-mix(in_srgb,var(--inyc-input-background-color),black_18%)] [:is(body.dark)_&]:border-[var(--inyc-outline-color)]"
              onClick={(e) => { e.stopPropagation(); onDuplicate(template); }}
            >
              duplicate
            </button>
          )}
          {onDelete && (
            <button 
              className="flex h-[32px] w-[5.5rem] items-center justify-center rounded-kit-full border border-transparent bg-danger-strong px-kit-8 text-kit-xs font-['Primary-Bold'] text-on-fill transition-opacity hover:opacity-90"
              onClick={(e) => { e.stopPropagation(); onDelete(template); }}
            >
              delete
            </button>
          )}
        </div>
      )}
      
      {/* Tap to use hint for picker mode */}
      {compact && onSelect && (
        <div className="text-center py-kit-8 text-kit-2xs text-[var(--inyc-primary-text-color)] opacity-50 border-t border-[var(--inyc-input-border-color)]">
          tap to use this template
        </div>
      )}
    </div>
  );
};

export default TemplateCard;
